import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { EMAIL_REGEX } from "../../../consts/regex";

import {
  checkCiDuplicate,
  checkEmailDuplicate,
  checkMobileNumberDuplicate,
} from "../../../services/authServices";

import useIdentityVerification from "../../../hooks/useIdentityVerification";
import useSimplePopup from "../../../hooks/useSimplePopup";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonInput from "../../../components/Input/CommonInput";
import BottomButtonGroup from "../../../components/Button/Bottom";
import { AuthHeader } from "../../../components/Layout/Auth/Header";

type Props = {};

export default function FindPassword(props: Props) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [
    {
      data: identifyData,
      isComplete: isCompleteIdentity,
      validate: validateIdentity,
    },
    identifyComponent,
  ] = useIdentityVerification({
    needDuplicate: true,
  });
  const [emailError, setEmailError] = useState<string>("");
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const [simplePopupComponent, { open }] = useSimplePopup({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateEmail = () => {
    if (!email?.length || email?.length <= 0) {
      setEmailError("이메일을 입력해주세요.");
      emailInputRef.current?.focus();
      return false;
    }
    if (!EMAIL_REGEX.test(email as string)) {
      setEmailError("이메일 형식이 올바르지 않습니다.");
      emailInputRef.current?.focus();
      return false;
    }

    return true;
  };

  return (
    <div className="px-5 pb-20">
      <StickyHeader
        title="비밀번호 찾기"
        onBackClick={() => navigate("/auth/login")}
      />
      <AuthHeader>
        <strong>비밀번호 찾기</strong>를<br />
        도와드릴게요
      </AuthHeader>

      <div className="pt-[30px] flex flex-col gap-5">
        <div className="flex flex-col">
          <div className="py-2.5 text-sm text-brand-neutral-60">
            아이디(이메일)
          </div>
          <CommonInput
            type="email"
            placeholder="이메일을 입력해주세요"
            value={email}
            onChange={(text) => {
              setEmail(text);
              setEmailError("");
            }}
            ref={emailInputRef}
            error={emailError}
          />
        </div>
        {identifyComponent}
      </div>

      <BottomButtonGroup
        title="인증하기"
        validation={isCompleteIdentity === false || !email}
        onClick={async () => {
          if (!validateEmail() || !validateIdentity()) return;

          try {
            const result = await checkEmailDuplicate({ email: email.trim() });
            if (
              result.status === HttpStatusCode.Ok &&
              result.data.isDuplicated === true
            ) {
              // nothing to do
            } else {
              open("가입한 적이 없는 이메일입니다.");
              return;
            }
          } catch (e: any) {
            if (e?.response?.data?.error) {
              open(e.response.data.error);
            } else {
              open("이메일 중복 체크에 실패했습니다.");
            }
            return;
          }

          try {
            const result = await checkMobileNumberDuplicate(
              identifyData.mdn as string
            );
            if (
              result.status === HttpStatusCode.Ok &&
              result.data.isDuplicated === true
            ) {
              // nothing to do
            } else {
              open("가입한 적이 없는 휴대폰 번호입니다.");
              return;
            }
          } catch (e: any) {
            if (e?.response?.data?.error) {
              open(e.response.data.error);
            } else {
              open("휴대폰 번호 중복 체크에 실패했습니다.");
            }
            return;
          }

          try {
            const result = await checkCiDuplicate(identifyData.ci as string);
            if (
              result.status === HttpStatusCode.Ok &&
              result.data.isDuplicated === true
            ) {
              // nothing to do
            } else {
              open("가입한 적이 없는 회원입니다.");
              return;
            }
          } catch (e: any) {
            if (e?.response?.data?.error) {
              open(e.response.data.error);
            } else {
              open("사용자 중복 체크에 실패했습니다.");
            }
            return;
          }

          navigate("/auth/findPassword/resetPassword", {
            state: {
              name: identifyData.name?.trim(),
              mobileNumber: identifyData.mdn,
              memberCi: identifyData.ci,
            },
          });
        }}
      />
      {simplePopupComponent}
    </div>
  );
}
